import { parseSgf } from './sgf.js';
import {
  NodeIdGenerator,
  buildInitialTree,
  playMove,
  serializeTree,
  deserializeTree,
  findNodeById,
  type GameNode,
  type BoardState,
  type Color,
  type SerializedNode
} from './engine.js';
import { draw, defaultTheme, getStarPoints, type DrawTheme, type StarPoint } from './render.js';

export interface RevealGobanMoveEventDetail {
  x: number;
  y: number;
  color: Color;
}

export interface RevealGobanNavigateEventDetail {
  direction: 'prev' | 'next';
  steps: number;
}

export type RevealGobanHoverEventDetail = { x: number; y: number } | null;

interface SyncMessage {
  type: 'playMove' | 'prev' | 'undo' | 'next' | 'reset' | 'hover' | 'syncRequest' | 'syncState';
  boardId: string;
  senderId: string;
  x?: number;
  y?: number;
  steps?: number;
  hover?: { x: number; y: number } | null;
  tree?: SerializedNode;
  currentNodeId?: string | null;
}

/**
 * Deterministic FNV-1a-style hash, used to derive a stable board-id from a
 * board's own content when none is given explicitly. Content-based (rather
 * than a load-order counter) so a main window and a speaker-notes window
 * loading the same deck agree on a board's identity regardless of exactly
 * when each one's markdown/async content happens to render.
 */
function hashFingerprint(input: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
}

const template = document.createElement('template');
template.innerHTML = `
  <style>
    :host {
      display: block;
      width: fit-content;
      margin: 0 auto;
      --reveal-goban-surface: #dcb35c;
      --reveal-goban-grid: #3a2a10;
      --reveal-goban-black-stone: #111111;
      --reveal-goban-white-stone: #f5f5f5;
      --reveal-goban-stone-stroke: #222222;
      --reveal-goban-marker: #3a2a10;
      --reveal-goban-accent: #b34b3c;
      --reveal-goban-text: #1a1a1a;
      --reveal-goban-text-muted: #55504a;
      --reveal-goban-button-bg: rgba(0, 0, 0, 0.04);
      --reveal-goban-button-bg-hover: rgba(0, 0, 0, 0.08);
      --reveal-goban-button-border: rgba(0, 0, 0, 0.2);
      --reveal-goban-font: system-ui, -apple-system, "Segoe UI", sans-serif;
      font-family: var(--reveal-goban-font);
    }
    .wrap {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
    }
    canvas {
      display: block;
      background: var(--reveal-goban-surface);
      border-radius: 2px;
      cursor: pointer;
      box-shadow: 0 22px 45px -18px rgba(0, 0, 0, 0.75), 0 0 0 1px rgba(10, 7, 5, 0.5);
    }
    .controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      box-sizing: border-box;
    }
    .nav-group {
      display: flex;
      gap: 6px;
    }
    button {
      padding: 0.4em 0.9em;
      border-radius: 3px;
      border: 1px solid var(--reveal-goban-button-border);
      background: var(--reveal-goban-button-bg);
      color: var(--reveal-goban-text-muted);
      font-family: inherit;
      font-size: 15px;
      letter-spacing: 0.03em;
      cursor: pointer;
      transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
    }
    button:hover:not(:disabled) {
      background: var(--reveal-goban-button-bg-hover);
      border-color: var(--reveal-goban-accent);
      color: var(--reveal-goban-text);
    }
    button:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    button.icon {
      font-size: 18px;
      line-height: 1;
      padding: 0.35em 0.7em;
    }
  </style>
  <div class="wrap" part="wrap">
    <canvas part="canvas"></canvas>
    <div class="controls" part="controls">
      <div class="nav-group">
        <button type="button" data-action="prev10" part="button">-10</button>
        <button type="button" data-action="prev" part="button">-1</button>
        <button type="button" data-action="next" part="button">+1</button>
        <button type="button" data-action="next10" part="button">+10</button>
      </div>
      <button type="button" class="icon" data-action="reset" part="button reset-button" title="Reset" aria-label="Reset">&#8630;</button>
    </div>
  </div>
`;

/** BroadcastChannel name for cross-window sync. Not configurable per
 * instance - boards already filter by board-id. */
const SYNC_CHANNEL_NAME = 'reveal-goban-sync';

/**
 * `<reveal-goban>` — an interactive, SGF-aware go/baduk/weiqi board.
 *
 * The SGF game record goes in the element's own text content, not an
 * attribute - see `#resolveSgfString()`. Other attributes: `size`, `move`,
 * `cell-size`, `board-id`, and the boolean `no-sync` (cross-window sync via
 * BroadcastChannel is on by default - e.g. a speaker-notes view mirroring
 * the main view - set this to opt out).
 *
 * Theming is done through CSS custom properties (see `--reveal-goban-*` in the
 * shadow stylesheet) and `::part(canvas|controls|button|reset-button|wrap)`.
 */
export class RevealGobanElement extends HTMLElement {
  static observedAttributes = ['size', 'move', 'cell-size', 'board-id', 'no-sync'];

  #canvas: HTMLCanvasElement;
  #controls: HTMLDivElement;
  #prev10Btn: HTMLButtonElement;
  #prevBtn: HTMLButtonElement;
  #nextBtn: HTMLButtonElement;
  #next10Btn: HTMLButtonElement;
  #resetBtn: HTMLButtonElement;

  #instanceId = `inst-${Math.random().toString(36).slice(2, 9)}-${Date.now()}`;
  #idGen = new NodeIdGenerator();
  #channel: BroadcastChannel | null = null;
  #boardId = '';

  #size = 9;
  #cell = 44;
  #padding = 44;
  #boardPx = 0;
  #rootNode: GameNode;
  #currentNode: GameNode;
  #hover: { x: number; y: number } | null = null;

  #connected = false;

  constructor() {
    super();
    const shadow = this.attachShadow({ mode: 'open' });
    shadow.appendChild(template.content.cloneNode(true));

    this.#canvas = shadow.querySelector('canvas') as HTMLCanvasElement;
    this.#controls = shadow.querySelector('.controls') as HTMLDivElement;
    this.#prev10Btn = shadow.querySelector('[data-action="prev10"]') as HTMLButtonElement;
    this.#prevBtn = shadow.querySelector('[data-action="prev"]') as HTMLButtonElement;
    this.#nextBtn = shadow.querySelector('[data-action="next"]') as HTMLButtonElement;
    this.#next10Btn = shadow.querySelector('[data-action="next10"]') as HTMLButtonElement;
    this.#resetBtn = shadow.querySelector('[data-action="reset"]') as HTMLButtonElement;

    this.#prev10Btn.addEventListener('click', () => this.prev(10));
    this.#prevBtn.addEventListener('click', () => this.prev(1));
    this.#nextBtn.addEventListener('click', () => this.next(1));
    this.#next10Btn.addEventListener('click', () => this.next(10));
    this.#resetBtn.addEventListener('click', () => this.reset());

    this.#canvas.addEventListener('mousemove', this.#onMouseMove);
    this.#canvas.addEventListener('mouseleave', this.#onMouseLeave);
    this.#canvas.addEventListener('click', this.#onClick);
    this.#canvas.addEventListener('contextmenu', this.#onContextMenu);

    // Placeholder tree until #rebuild() runs in connectedCallback; keeps
    // fields non-null for TypeScript without an unsafe `!` everywhere.
    const empty = buildInitialTree(this.#size, null, null, this.#idGen);
    this.#rootNode = empty.rootNode;
    this.#currentNode = empty.currentNode;
  }

  connectedCallback(): void {
    this.#connected = true;
    this.#rebuild();
    this.#setupChannel();
  }

  disconnectedCallback(): void {
    this.#teardownChannel();
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (!this.#connected || oldValue === newValue) return;
    if (name === 'board-id' || name === 'no-sync') {
      this.#teardownChannel();
      this.#boardId = this.#resolveBoardId();
      this.#setupChannel();
      return;
    }
    this.#rebuild();
  }

  // ---------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------

  playMove(x: number, y: number): boolean {
    return this.#playMove(x, y, false);
  }

  /**
   * If (x, y) currently has a stone, jumps to the move where it was
   * placed — the most recent one, if that point was played, captured, and
   * played again. Returns false if the point is empty or already the
   * current move.
   */
  goToStone(x: number, y: number): boolean {
    return this.#goToStone(x, y);
  }

  undo(steps = 1): boolean {
    return this.prev(steps);
  }

  prev(steps = 1): boolean {
    return this.#prev(steps, false);
  }

  next(steps = 1): boolean {
    return this.#next(steps, false);
  }

  reset(): void {
    this.#reset(false);
  }

  setHover(x: number | null, y: number | null): void {
    this.#setHover(x, y, false);
  }

  getHover(): RevealGobanHoverEventDetail {
    return this.#hover ? { x: this.#hover.x, y: this.#hover.y } : null;
  }

  canPrev(): boolean {
    return !!this.#currentNode.parent;
  }

  canNext(): boolean {
    return this.#currentNode.children.length > 0;
  }

  getStarPoints(): StarPoint[] {
    return getStarPoints(this.#size);
  }

  getState(): BoardState {
    return this.#currentNode.state.map((row) => row.slice()) as BoardState;
  }

  getTurn(): Color {
    return this.#currentNode.turn;
  }

  getHistory(): { state: BoardState; turn: Color; lastMove: { x: number; y: number } | null }[] {
    const list: { state: BoardState; turn: Color; lastMove: { x: number; y: number } | null }[] = [];
    let node = this.#currentNode.parent;
    while (node) {
      list.unshift({
        state: node.state.map((row) => row.slice()) as BoardState,
        turn: node.turn,
        lastMove: node.lastMove ? { x: node.lastMove.x, y: node.lastMove.y } : null
      });
      node = node.parent;
    }
    return list;
  }

  getLastMove(): { x: number; y: number } | null {
    return this.#currentNode.lastMove ? { x: this.#currentNode.lastMove.x, y: this.#currentNode.lastMove.y } : null;
  }

  getCurrentNode(): GameNode {
    return this.#currentNode;
  }

  // Named getRootGameNode (not getRootNode) to avoid colliding with the
  // native Element.getRootNode() method.
  getRootGameNode(): GameNode {
    return this.#rootNode;
  }

  getBoardId(): string {
    return this.#boardId;
  }

  getInstanceId(): string {
    return this.#instanceId;
  }

  // ---------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------

  // The SGF game record is the element's light-DOM text content, trimmed.
  // Safe to read in connectedCallback because this package's only supported
  // registration path is a deferred `type="module"` script, which always
  // runs after the document (and any markdown-rendered content) has
  // finished parsing.
  #resolveSgfString(): string | null {
    const text = this.textContent?.trim();
    return text ? text : null;
  }

  #resolveBoardId(): string {
    const explicit = this.getAttribute('board-id') || this.id;
    if (explicit) return explicit;
    const fingerprint = [
      this.#resolveSgfString() ?? '',
      this.getAttribute('size') ?? '',
      this.getAttribute('move') ?? '',
      this.getAttribute('cell-size') ?? ''
    ].join('|');
    return `reveal-goban-${hashFingerprint(fingerprint)}`;
  }

  #rebuild(): void {
    const sgfString = this.#resolveSgfString();
    const sgfData = sgfString ? parseSgf(sgfString) : null;

    const sizeAttr = this.getAttribute('size');
    this.#size = sizeAttr ? parseInt(sizeAttr, 10) : sgfData?.size ?? 9;

    const moveAttr = this.getAttribute('move');
    const targetMove = moveAttr !== null && moveAttr !== '' ? parseInt(moveAttr, 10) : null;

    const cellAttr = this.getAttribute('cell-size');
    // The host page may render this element at any physical scale, so a
    // narrow viewport gets a proportionally larger cell size to stay
    // legible/tappable rather than shrinking along with everything else.
    const isCompactViewport =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 600px)').matches;
    const compactTargetBoardPx = 880;
    const defaultCell = isCompactViewport
      ? Math.round(compactTargetBoardPx / (this.#size + 1))
      : this.#size <= 9
        ? 44
        : this.#size <= 13
          ? 32
          : 22;
    this.#cell = cellAttr ? parseInt(cellAttr, 10) : defaultCell;
    this.#padding = this.#cell;
    this.#boardPx = this.#padding * 2 + this.#cell * (this.#size - 1);

    this.#idGen = new NodeIdGenerator();
    const tree = buildInitialTree(this.#size, sgfData, targetMove, this.#idGen);
    this.#rootNode = tree.rootNode;
    this.#currentNode = tree.currentNode;
    this.#hover = null;
    this.#boardId = this.#resolveBoardId();
    if (!this.getAttribute('board-id') && !this.id) {
      this.setAttribute('board-id', this.#boardId);
    }

    this.#canvas.width = this.#boardPx;
    this.#canvas.height = this.#boardPx;
    this.#controls.style.width = `${this.#boardPx}px`;

    this.#draw();
    this.#updateControls();
  }

  #readTheme(): DrawTheme {
    const style = getComputedStyle(this);
    const get = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback;
    return {
      gridColor: get('--reveal-goban-grid', defaultTheme.gridColor),
      starPointColor: get('--reveal-goban-grid', defaultTheme.starPointColor),
      blackStone: get('--reveal-goban-black-stone', defaultTheme.blackStone),
      whiteStone: get('--reveal-goban-white-stone', defaultTheme.whiteStone),
      stoneStroke: get('--reveal-goban-stone-stroke', defaultTheme.stoneStroke),
      lastMoveMarkerOnBlack: get('--reveal-goban-white-stone', defaultTheme.lastMoveMarkerOnBlack),
      lastMoveMarkerOnWhite: get('--reveal-goban-black-stone', defaultTheme.lastMoveMarkerOnWhite),
      hoverHighlight: get('--reveal-goban-accent', defaultTheme.hoverHighlight),
      markerColor: get('--reveal-goban-marker', defaultTheme.markerColor)
    };
  }

  #draw(): void {
    const ctx = this.#canvas.getContext('2d');
    if (!ctx) return;
    draw({
      ctx,
      size: this.#size,
      cell: this.#cell,
      padding: this.#padding,
      boardPx: this.#boardPx,
      state: this.#currentNode.state,
      lastMove: this.#currentNode.lastMove,
      hover: this.#hover,
      turn: this.#currentNode.turn,
      marks: this.#currentNode.marks,
      theme: this.#readTheme()
    });
  }

  #updateControls(): void {
    const canPrev = this.canPrev();
    const canNext = this.canNext();
    this.#prev10Btn.disabled = !canPrev;
    this.#prevBtn.disabled = !canPrev;
    this.#nextBtn.disabled = !canNext;
    this.#next10Btn.disabled = !canNext;
  }

  #setupChannel(): void {
    // Idempotent: #rebuild() may reflect an auto-generated board-id back as
    // an attribute, which synchronously re-enters attributeChangedCallback
    // (and thus this method) mid-connect. Without this guard that opens a
    // second BroadcastChannel and leaks the first one's listener.
    if (this.#channel) return;
    if (this.getAttribute('no-sync') !== null) return;
    if (typeof BroadcastChannel === 'undefined') return;
    try {
      this.#channel = new BroadcastChannel(SYNC_CHANNEL_NAME);
    } catch {
      this.#channel = null;
      return;
    }
    this.#channel.addEventListener('message', this.#onChannelMessage);
    this.#postMessage({ type: 'syncRequest', boardId: this.#boardId, senderId: this.#instanceId });
  }

  #teardownChannel(): void {
    if (!this.#channel) return;
    this.#channel.removeEventListener('message', this.#onChannelMessage);
    this.#channel.close();
    this.#channel = null;
  }

  #postMessage(message: SyncMessage): void {
    this.#channel?.postMessage(message);
  }

  #onChannelMessage = (event: MessageEvent<SyncMessage>): void => {
    const msg = event.data;
    if (!msg || msg.boardId !== this.#boardId || msg.senderId === this.#instanceId) return;

    switch (msg.type) {
      case 'playMove':
        if (typeof msg.x === 'number' && typeof msg.y === 'number') this.#playMove(msg.x, msg.y, true);
        break;
      case 'prev':
      case 'undo':
        this.#prev(msg.steps || 1, true);
        break;
      case 'next':
        this.#next(msg.steps || 1, true);
        break;
      case 'reset':
        this.#reset(true);
        break;
      case 'hover':
        if (msg.hover && typeof msg.hover.x === 'number' && typeof msg.hover.y === 'number') {
          this.#setHover(msg.hover.x, msg.hover.y, true);
        } else {
          this.#setHover(null, null, true);
        }
        break;
      case 'syncRequest':
        this.#postMessage({
          type: 'syncState',
          boardId: this.#boardId,
          senderId: this.#instanceId,
          tree: serializeTree(this.#rootNode) ?? undefined,
          currentNodeId: this.#currentNode.id
        });
        break;
      case 'syncState':
        if (msg.tree) {
          const root = deserializeTree(msg.tree, null);
          if (root) {
            this.#rootNode = root;
            this.#currentNode = findNodeById(root, msg.currentNodeId) || root;
            this.#draw();
            this.#updateControls();
          }
        }
        break;
    }
  };

  #setHover(gx: number | null, gy: number | null, fromSync: boolean): void {
    const newHover =
      gx !== null && gy !== null && gx >= 0 && gx < this.#size && gy >= 0 && gy < this.#size ? { x: gx, y: gy } : null;

    const unchanged =
      (!this.#hover && !newHover) || (this.#hover && newHover && this.#hover.x === newHover.x && this.#hover.y === newHover.y);
    if (unchanged) return;

    this.#hover = newHover;
    this.#draw();
    this.#emit<RevealGobanHoverEventDetail>('reveal-goban-hover', newHover);

    if (!fromSync) {
      this.#postMessage({ type: 'hover', boardId: this.#boardId, senderId: this.#instanceId, hover: newHover });
    }
  }

  #prev(steps: number, fromSync: boolean): boolean {
    let moved = 0;
    while (moved < steps && this.#currentNode.parent) {
      this.#currentNode = this.#currentNode.parent;
      moved++;
    }
    if (moved === 0) return false;

    this.#draw();
    this.#updateControls();
    this.#emit<RevealGobanNavigateEventDetail>('reveal-goban-navigate', { direction: 'prev', steps: moved });
    if (!fromSync) this.#postMessage({ type: 'prev', boardId: this.#boardId, senderId: this.#instanceId, steps: moved });
    return true;
  }

  #next(steps: number, fromSync: boolean): boolean {
    let moved = 0;
    while (moved < steps && this.#currentNode.children.length > 0) {
      this.#currentNode = this.#currentNode.children[this.#currentNode.children.length - 1] as GameNode;
      moved++;
    }
    if (moved === 0) return false;

    this.#draw();
    this.#updateControls();
    this.#emit<RevealGobanNavigateEventDetail>('reveal-goban-navigate', { direction: 'next', steps: moved });
    if (!fromSync) this.#postMessage({ type: 'next', boardId: this.#boardId, senderId: this.#instanceId, steps: moved });
    return true;
  }

  #playMove(gx: number, gy: number, fromSync: boolean): boolean {
    const color = this.#currentNode.turn;
    const nextNode = playMove(this.#currentNode, this.#size, gx, gy, color, this.#idGen);
    if (!nextNode) return false;
    this.#currentNode = nextNode;

    this.#draw();
    this.#updateControls();
    this.#emit<RevealGobanMoveEventDetail>('reveal-goban-move', { x: gx, y: gy, color });
    if (!fromSync) this.#postMessage({ type: 'playMove', boardId: this.#boardId, senderId: this.#instanceId, x: gx, y: gy });
    return true;
  }

  /**
   * Walks from the current node toward the root looking for the nearest
   * (i.e. most recent) move played at (x, y). Since a `.move` entry is only
   * ever written by actually playing a stone there - never by the capture
   * that might later clear it - the first match found walking backward is
   * necessarily the placement still on the board, even if that point was
   * played, captured, and played again earlier in the game. If no move
   * matches all the way to the root, a stone there must come from SGF setup
   * (AB/AW) on the root itself.
   */
  #findPlacementNode(x: number, y: number): GameNode | null {
    let node: GameNode | null = this.#currentNode;
    while (node) {
      if (node.move && !node.move.pass && node.move.x === x && node.move.y === y) return node;
      if (!node.parent) return node.state[y]?.[x] ? node : null;
      node = node.parent;
    }
    return null;
  }

  // Always locally triggered (a click, or a direct goToStone() call) - never
  // a reaction to an incoming sync message, since the resulting #prev()
  // call already broadcasts a plain 'prev' that the other side replays
  // through its own #prev(), landing on the same node by construction.
  #goToStone(x: number, y: number): boolean {
    const target = this.#findPlacementNode(x, y);
    if (!target || target === this.#currentNode) return false;

    let steps = 0;
    let node: GameNode | null = this.#currentNode;
    while (node && node !== target) {
      node = node.parent;
      steps++;
    }
    if (node !== target) return false; // defensive: target should always be an ancestor

    return this.#prev(steps, false);
  }

  #reset(fromSync: boolean): void {
    const sgfString = this.#resolveSgfString();
    const sgfData = sgfString ? parseSgf(sgfString) : null;
    const moveAttr = this.getAttribute('move');
    const targetMove = moveAttr !== null && moveAttr !== '' ? parseInt(moveAttr, 10) : null;

    this.#idGen = new NodeIdGenerator();
    const tree = buildInitialTree(this.#size, sgfData, targetMove, this.#idGen);
    this.#rootNode = tree.rootNode;
    this.#currentNode = tree.currentNode;
    this.#hover = null;

    this.#draw();
    this.#updateControls();
    this.#emit<void>('reveal-goban-reset', undefined);
    if (!fromSync) this.#postMessage({ type: 'reset', boardId: this.#boardId, senderId: this.#instanceId });
  }

  #emit<T>(name: string, detail: T): void {
    this.dispatchEvent(new CustomEvent<T>(name, { detail, bubbles: true, composed: true }));
  }

  #canvasCoords(e: MouseEvent): { gx: number; gy: number } {
    const rect = this.#canvas.getBoundingClientRect();
    const mx = (e.clientX - rect.left) * (this.#canvas.width / rect.width);
    const my = (e.clientY - rect.top) * (this.#canvas.height / rect.height);
    return {
      gx: Math.round((mx - this.#padding) / this.#cell),
      gy: Math.round((my - this.#padding) / this.#cell)
    };
  }

  #onMouseMove = (e: MouseEvent): void => {
    const { gx, gy } = this.#canvasCoords(e);
    if (gx >= 0 && gx < this.#size && gy >= 0 && gy < this.#size) {
      this.#setHover(gx, gy, false);
    } else {
      this.#setHover(null, null, false);
    }
  };

  #onMouseLeave = (): void => {
    this.#setHover(null, null, false);
  };

  #onClick = (e: MouseEvent): void => {
    const { gx, gy } = this.#canvasCoords(e);
    const inBounds = gx >= 0 && gx < this.#size && gy >= 0 && gy < this.#size;
    const occupied = inBounds && this.#currentNode.state[gy]?.[gx] !== 0;
    if (occupied) {
      this.#goToStone(gx, gy);
    } else {
      this.#playMove(gx, gy, false);
    }
  };

  #onContextMenu = (e: MouseEvent): void => {
    e.preventDefault();
    this.#prev(1, false);
  };
}
